import type { Message } from "./types.js";

// seq:0 rows (pending bubbles, notes; not failed sends) last; then createdAt, seq, id: a total order.
// Moved here from state.svelte.ts (knowledge/88 Phase 0) — pure, no runes.
export function compareMessages(a: Message, b: Message): number {
  const aPinned = isPinned(a);
  if (aPinned !== isPinned(b)) return aPinned ? 1 : -1;
  if (a.createdAt !== b.createdAt) return a.createdAt - b.createdAt;
  const bySeq = (a.seq ?? 0) - (b.seq ?? 0);
  if (bySeq !== 0) return bySeq;
  if (a.id === b.id) return 0;
  return a.id < b.id ? -1 : 1;
}

function isPinned(m: Message): boolean {
  return (m.seq ?? 0) === 0 && m.sendStatus !== "failed";
}

const MAX_CLOCK_SKEW_MS = 24 * 60 * 60 * 1000;

// After every row shown; a server row over a day ahead is a bad time, an own send never is.
export function bubbleTime(messages: Message[], now: number): number {
  const ceiling = now + MAX_CLOCK_SKEW_MS;
  let floor = now;
  for (const m of messages) {
    const counted = m.createdAt <= ceiling || m.id.startsWith("local-");
    if (counted && m.createdAt >= floor) floor = m.createdAt + 1;
  }
  return floor;
}

// Insert `msg` into an ALREADY-SORTED `messages` (the bulk entry points —
// setMessages/prependMessages/mergeServer — establish that invariant with a full
// sort). Common case (live broadcasts, drainSync page replay) arrives in order →
// O(1) append; out-of-order arrivals (seq_gap, cross-instance relay) take an
// O(log n) binary-search insert. Replaces the per-broadcast copy+O(n log n) sort
// in addMessage (knowledge/88 Phase 0).
//
// Always returns a FRESH array: MessageList's $effect.pre tracks the list by
// reference, so an in-place push on the $state proxy would not re-trigger it.
export function insertMessageSorted(messages: Message[], msg: Message): Message[] {
  if (messages.length === 0 || compareMessages(messages[messages.length - 1], msg) <= 0) {
    return [...messages, msg];
  }
  // Upper-bound binary search: first index that sorts strictly after `msg`, so a
  // late arrival lands after its equals — same placement a stable full sort of
  // [...messages, msg] would produce.
  let lo = 0;
  let hi = messages.length;
  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    if (compareMessages(messages[mid], msg) <= 0) lo = mid + 1;
    else hi = mid;
  }
  return [...messages.slice(0, lo), msg, ...messages.slice(lo)];
}

export function replaceSorted(messages: Message[], idx: number, row: Message): Message[] {
  return insertMessageSorted([...messages.slice(0, idx), ...messages.slice(idx + 1)], row);
}

// Row `idx` with a new send status, plus the refusal reason when one is given.
export function withSendStatus(
  messages: Message[],
  idx: number,
  status: NonNullable<Message["sendStatus"]>,
  reason?: string,
): Message[] {
  const row = { ...messages[idx], sendStatus: status };
  return replaceSorted(
    messages,
    idx,
    reason === undefined ? row : { ...row, sendFailedReason: reason },
  );
}

// Match an incoming server message to the optimistic `local-` row it settles
// (#501). Prefer the client-generated `clientMsgId` (exact — disambiguates two
// identical consecutive sends and retries). The `(fromId, text)` fallback is for
// rows that carry NO client id at all (a pre-CYBORG-798 row, or one written by a
// path that never stamps one, e.g. an agent) — CYBORG-801: that fallback is ONLY
// ever allowed to match an optimistic row that ALSO has no client id. A pending
// row that DOES carry a client id can only ever be settled by that exact id,
// never by author+text, whether the incoming message is tagged with a
// *different* id or untagged entirely — otherwise a same-text resend ("ok",
// "ok") lets an untagged/differently-tagged refetched row cannibalize the wrong
// pending bubble (the reconnect-delete bug this ticket fixes). Returns -1 when
// `msg` is itself a local row or no optimistic match exists.
//
// Pure (no Svelte runes) so it's unit-testable in isolation from the state store.
export function findOptimisticIndex(messages: Message[], msg: Message): number {
  if (msg.id.startsWith("local-")) return -1;
  if (msg.clientMsgId) {
    const exact = messages.findIndex(
      (m) => m.id.startsWith("local-") && m.clientMsgId === msg.clientMsgId,
    );
    if (exact >= 0) return exact;
  }
  // Untagged fallback — reached when `msg` has no client id, OR it has one that
  // didn't exact-match anything. Either way it may ONLY settle an equally
  // untagged pending row; a tagged pending row is exempt no matter what.
  return messages.findIndex(
    (m) =>
      m.id.startsWith("local-") && !m.clientMsgId && m.fromId === msg.fromId && m.text === msg.text,
  );
}

// Stable DOM identity for a message row, used as the `{#each}` key in MessageList.
//
// Keying by `message.id` alone remounts a row on reconcile: an optimistic bubble
// is created with id `local-<clientMsgId>` and, when the server echo settles it,
// its id flips to the server id (`state.svelte.ts` addMessage). A keyed `{#each}`
// treats that as a NEW row — it destroys and recreates the node, so the message
// visibly flashes / disappears for a frame while the user is reading (#msg-remount).
//
// `clientMsgId` is stamped on the optimistic bubble AND echoed by the relay, and
// the reconcile preserves it, so it is stable across the local→server id swap.
// Prefer it (namespaced to avoid colliding with a raw id); fall back to the id
// for peer messages / older relays that carry no clientMsgId.
export function messageKey(m: Message): string {
  return m.clientMsgId ? `c:${m.clientMsgId}` : m.id;
}

// A re-applied optimistic bubble (`local-<clientMsgId>`) whose send has ALREADY
// settled to a server row carrying the same clientMsgId must not be inserted
// again: it would duplicate the message and — because messageKey() keys both by
// that clientMsgId — produce a duplicate `{#each}` key. Reproduces when a reloaded
// outbox bubble (reapplyOutboxBubbles) races its own server echo already present
// in the list. Pure so the store can guard addMessage with it.
export function isSettledDuplicate(messages: Message[], msg: Message): boolean {
  if (!msg.id.startsWith("local-") || !msg.clientMsgId) return false;
  return messages.some((m) => !m.id.startsWith("local-") && m.clientMsgId === msg.clientMsgId);
}

// Rows a full tail replace must keep: unsettled `local-` sends, and server rows that reached the
// window while the read was in flight (seq above `seqAtRequest`), are not in the tail and sort at
// or after its oldest row; an older one would be an island above a gap, so the pager owns it.
export function rowsTheTailMissed(
  window: Message[],
  tail: Message[],
  seqAtRequest: number,
): Message[] {
  const tailIds = new Set(tail.map((m) => m.id));
  const tailClientIds = new Set(tail.map((m) => m.clientMsgId).filter(Boolean));
  const floor = tail.reduce<Message | undefined>(
    (min, m) => (!min || compareMessages(m, min) < 0 ? m : min),
    undefined,
  );
  return window.filter((m) =>
    m.id.startsWith("local-")
      ? !(m.clientMsgId && tailClientIds.has(m.clientMsgId))
      : m.seq > seqAtRequest && !tailIds.has(m.id) && (!floor || compareMessages(m, floor) >= 0),
  );
}

// The newest row of a fetched page: the newer-edge cursor (a live row or a `local-` bubble at
// the end of the window is not a server position, so paging "after" it closes the gap for good).
export function pageTailId(page: Message[]): string | undefined {
  let tail: Message | undefined;
  for (const m of page)
    if (!m.id.startsWith("local-") && (!tail || compareMessages(m, tail) > 0)) tail = m;
  return tail?.id;
}

// Cursor for a load-newer page: the last page's tail, else the newest SERVER row in the window.
export function newerPageCursor(
  fetchedTailId: string | undefined,
  window: Message[],
): string | undefined {
  return fetchedTailId ?? window.findLast((m) => !m.id.startsWith("local-"))?.id;
}
